import { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ServicesProvider, type Services } from './app/services';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginPage, AuthLayout } from './auth/LoginPage';
import { MfaPage } from './auth/MfaPage';
import { ClubPickerPage, StartRedirect } from './auth/ClubPickerPage';
import { ClubGuard, ClubIndex, ClubLayout } from './app/layouts';
import { SURFACE, surfaceBlock } from './lib/surface';
import { Button, Loading, ToastProvider } from './components/ui';
import { DashboardPage } from './pages/club/DashboardPage';
import { DoorsPage } from './pages/club/DoorsPage';
import { PersonsPage } from './pages/club/PersonsPage';
import { PersonDetailPage } from './pages/club/PersonDetailPage';
import { GroupsPage } from './pages/club/GroupsPage';
import { GroupDetailPage } from './pages/club/GroupDetailPage';
import { CodesPage } from './pages/club/CodesPage';
import { LoanCardsPage } from './pages/club/LoanCardsPage';
import { HelpPage } from './pages/club/HelpPage';
import { LogbookPage } from './pages/club/LogbookPage';
import { ImportPage } from './pages/club/ImportPage';
import { SettingsPage } from './pages/club/SettingsPage';

function AuthGate() {
  const { status, error, signOut } = useAuth();
  if (status === 'loading') return <Loading />;
  if (status === 'signed_out') return <LoginPage />;
  if (status === 'wrong_surface') return <WrongSurface />;
  if (status === 'needs_mfa') return <MfaPage />;
  if (status === 'error') {
    return (
      <AuthLayout>
        <h2>Inloggen lukt niet</h2>
        <div className="alert alert-danger" role="alert" style={{ margin: '16px 0' }}>
          {error}
        </div>
        <Button onClick={() => void signOut()}>Opnieuw inloggen</Button>
      </AuthLayout>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<StartRedirect />} />
      <Route path="/clubs" element={<ClubPickerPage />} />
      <Route path="/c/:orgId" element={<ClubLayout />}>
        <Route index element={<ClubIndex />} />
        <Route path="dashboard" element={<ClubGuard route="dashboard"><DashboardPage /></ClubGuard>} />
        <Route path="deuren" element={<ClubGuard route="doors"><DoorsPage /></ClubGuard>} />
        <Route path="personen" element={<ClubGuard route="persons"><PersonsPage /></ClubGuard>} />
        <Route path="personen/:personId" element={<ClubGuard route="persons"><PersonDetailPage /></ClubGuard>} />
        <Route path="groepen" element={<ClubGuard route="groups"><GroupsPage /></ClubGuard>} />
        <Route path="groepen/:groupId" element={<ClubGuard route="groups"><GroupDetailPage /></ClubGuard>} />
        <Route path="codes" element={<ClubGuard route="codes"><CodesPage /></ClubGuard>} />
        <Route path="uitleenpassen" element={<ClubGuard route="loans"><LoanCardsPage /></ClubGuard>} />
        <Route path="hulp" element={<ClubGuard route="help"><HelpPage /></ClubGuard>} />
        <Route path="logboek" element={<ClubGuard route="logbook"><LogbookPage /></ClubGuard>} />
        <Route path="import" element={<ClubGuard route="import"><ImportPage /></ClubGuard>} />
        <Route path="instellingen" element={<ClubGuard route="settings"><SettingsPage /></ClubGuard>} />
      </Route>
      {PlatformRoutes && (
        <Route
          path="/platform/*"
          element={
            <Suspense fallback={<Loading />}>
              <PlatformRoutes />
            </Suspense>
          }
        />
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// In de club-build (beheer.antreja.nl) is __PLATFORM_UI__ false: de Antreja-schermen worden dan niet meegebouwd.
const PlatformRoutes = __PLATFORM_UI__ ? lazy(() => import('./PlatformRoutes')) : null;

export function App({ services }: { services: Services }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: services.mode === 'supabase' } },
      }),
  );
  return (
    <ServicesProvider services={services}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <AuthGate />
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ServicesProvider>
  );
}

/** Ingelogd in de verkeerde omgeving (Antreja-account op beheer.antreja.nl of clubaccount op admin.antreja.nl). */
function WrongSurface() {
  const { session, signOut } = useAuth();
  const block = surfaceBlock(SURFACE, session?.platformRole ?? null);
  return (
    <AuthLayout>
      <h2>{block?.title ?? 'Verkeerde omgeving'}</h2>
      <p className="muted" style={{ margin: '12px 0 20px' }}>
        {block?.text}
      </p>
      <div className="stack">
        {block && (
          <a className="btn btn-primary" href={block.url} style={{ textAlign: 'center' }}>
            Naar {block.url.replace('https://', '')}
          </a>
        )}
        <Button onClick={() => void signOut()}>Uitloggen</Button>
      </div>
    </AuthLayout>
  );
}
